import { ReceivedInvoicesSearchQuery } from '@/context/services/invoice/type';
import { useOfficeMemberOptions } from '@/context/services/invoice/useOfficeMemberOptions';
import { useApPayeeInitialData } from '@/features/invoices/components/List/Search/hooks';
import { BaseOption } from '@/hooks/useTypeToOptions';
import { useEffect, useMemo, useRef, useState } from 'react';

const toNameLookup = (options: BaseOption[]): Record<string, string> =>
  Object.fromEntries(
    options.map((option) => [String(option.value), String(option.label)])
  );

const usePicNames = (picIds: string[]): Record<string, string> => {
  const [picOptions, setPicOptions] = useState<BaseOption[]>([]);
  const previousPicIdsRef = useRef<string[]>([]);
  const { fetchOfficeMembers } = useOfficeMemberOptions();

  useEffect(() => {
    const isDifferent =
      picIds.length !== previousPicIdsRef.current.length ||
      picIds.some((id, index) => previousPicIdsRef.current[index] !== id);

    if (!isDifferent) return;

    previousPicIdsRef.current = [...picIds];
    if (picIds.length === 0) {
      setPicOptions([]);
      return;
    }

    const requestedPicIds = [...picIds];
    fetchOfficeMembers(requestedPicIds).then((options) => {
      const isCurrentRequest =
        requestedPicIds.length === previousPicIdsRef.current.length &&
        requestedPicIds.every(
          (id, index) => previousPicIdsRef.current[index] === id
        );
      if (isCurrentRequest) setPicOptions(options);
    });
    // fetchOfficeMembers is recreated with the generated client.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picIds]);

  return useMemo(() => toNameLookup(picOptions), [picOptions]);
};

export const useResolvedAppliedSearchNames = (
  searchQuery: ReceivedInvoicesSearchQuery
) => {
  const payeeOptions = useApPayeeInitialData(searchQuery.ap_payee_id_in ?? []);
  const picNames = usePicNames(searchQuery.pic_id_in ?? []);
  const payeeNames = useMemo(() => toNameLookup(payeeOptions), [payeeOptions]);

  return {
    payeeNames,
    picNames,
  };
};
